import React, { FC, Fragment, useState } from 'react';
import { Box, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { GroupFragment, ReportingFilter, ReportNames, usePostOverviewStatsQuery } from 'api';
import { useDateRange, useNotify, useProtectedClient, useReportingFilter } from 'hooks';
import { DatePickers, PaddedPaper } from 'components';

import {
  DailyPostStatsChart,
  FilterChips,
  Filters,
  NewApprovedPostChart,
  PostDashboardStatTable,
} from '../components/Analytics';
import { ClearFiltersButton, DownloadReportButton, RangePickerWrapper } from '../components/shared';

const AnalyticsPosts: FC = () => {
  const client = useProtectedClient();
  const notify = useNotify();
  const { t } = useTranslation();
  const [cachedGroups, setCachedGroups] = useState([]);
  const [requestReport, setRequestReport] = useState(false);
  const { startDate, setStartDate, endDate, setEndDate, dateRange, setDateRange } = useDateRange();
  const { filter, flattenedFilter, clearFilter, setFilter, removeFilterItem } =
    useReportingFilter();

  usePostOverviewStatsQuery(
    {
      client_id: client.id,
      start_date: startDate,
      end_date: endDate,
      report: ReportNames.Posts,
      filter,
    },
    {
      enabled: requestReport,
      onError: () => notify.error({ message: t('analytics:reportRequestedError') }),
      onSettled: () => setRequestReport(false),
    }
  );

  function handleReportRequest() {
    notify.info({ message: t('analytics:reportRequested') });
    setRequestReport(true);
  }

  function handleApplyFilter(nextFilter: ReportingFilter, selectedGroups: GroupFragment[]) {
    setCachedGroups(selectedGroups);
    setFilter(nextFilter);
  }

  function handleRemoveFilterItem(id: string) {
    const removedGroupArray = cachedGroups.filter(cachedGroup => cachedGroup.id !== id);

    setCachedGroups(removedGroupArray);
    removeFilterItem(id);
  }

  return (
    <Fragment>
      <Box mb={2}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid>
            <Typography variant="h5" color="textSecondary">
              {t('analytics:approved_new_posts')}
            </Typography>
          </Grid>
          <Grid>
            <DownloadReportButton onClick={handleReportRequest} />
          </Grid>
        </Grid>
      </Box>
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid>
          <PaddedPaper>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid>
                <Grid container spacing={2}>
                  <Grid>
                    <RangePickerWrapper>
                      <DatePickers.Range
                        dateRange={dateRange}
                        startDate={startDate}
                        endDate={endDate}
                        onSetStart={setStartDate}
                        onSetEnd={setEndDate}
                        onSetRange={setDateRange}
                      />
                    </RangePickerWrapper>
                  </Grid>
                  <Grid>
                    <Box display="flex" height="100%">
                      <Filters
                        filter={filter}
                        onSubmit={handleApplyFilter}
                        selectedGroups={cachedGroups}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              {!!flattenedFilter.length && (
                <Grid>
                  <ClearFiltersButton onClick={clearFilter} />
                </Grid>
              )}
            </Grid>
            {!!flattenedFilter.length && (
              <Grid container>
                <Grid>
                  <FilterChips
                    flattenedFilter={flattenedFilter}
                    onRemove={handleRemoveFilterItem}
                    selectedGroups={cachedGroups}
                  />
                </Grid>
              </Grid>
            )}
          </PaddedPaper>
        </Grid>
        <Grid container spacing={3}>
          <Grid xs={4}>
            <NewApprovedPostChart startDate={startDate} endDate={endDate} filter={filter} />
          </Grid>
          <Grid xs={8}>
            <DailyPostStatsChart startDate={startDate} endDate={endDate} filter={filter} />
          </Grid>
        </Grid>
        <Grid>
          <PostDashboardStatTable startDate={startDate} endDate={endDate} filter={filter} />
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default AnalyticsPosts;
